import React, { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';

// Build base correctly: ENV is the origin (no path), then append /shuttle
const API_ORIGIN =
  process.env.REACT_APP_API_BASE || 'https://api.karrentals.ca';
const API_BASE = `${API_ORIGIN}/shuttle`;

const PRIVATE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScB_wBC_P8LivyBE_rox_LlzB51e3Ym2yeu2GAR71s9HMWyEw/viewform?usp=dialog';

// --- Private bookings data ---
const PRIVATE_RATES = [
  { name: 'Big White', oneway: 500, round: 850 },
  { name: 'Revelstoke', oneway: 900, round: 1500 },
  { name: 'Silverstar', oneway: 600, round: 900 },
];

const HOURLY = {
  minHours: 4,
  standardPerHour: 195,
  after10pmPerHour: 225,
  day8Hours: 1300,
  lastMinuteSurchargePct: 20,
};

// --- Memberships data ---
const MEMBERSHIP_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScB_wBC_P8LivyBE_rox_LlzB51e3Ym2yeu2GAR71s9HMWyEw/viewform?usp=dialog';

const SearchBar = ({ onSearch, routesUrl = `${API_BASE}/destinations` }) => {
  const navigate = useNavigate();

  const [tripType, setTripType] = useState('return'); // 'oneway' | 'return'
  const [pickupDateTime, setPickupDateTime] = useState(null);

  // destinations list
  const [destinations, setDestinations] = useState([]);
  const [selectedDestinationId, setSelectedDestinationId] = useState('');
  const [loadingDest, setLoadingDest] = useState(true);
  const [error, setError] = useState('');
  const [empty, setEmpty] = useState(false);

  // search params
  const [passengers, setPassengers] = useState(1);

  // inline results
  const [query, setQuery] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [days, setDays] = useState([]); // [{date, leg, slots:[{time_slot,seats_left,price_cents,currency}]}]
  const [destinationMeta, setDestinationMeta] = useState(null);

  // selection for return: one slot per LEG, not per date
  // { outbound: {date,time_slot,price_cents,currency}, return: {...} }
  const [selectedLegSlots, setSelectedLegSlots] = useState({});

  // confirm modals
  const [confirmOpen, setConfirmOpen] = useState(false); // oneway
  const [confirmData, setConfirmData] = useState(null); // { date, slot }
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);

  // panels
  const [showPrivate, setShowPrivate] = useState(false);
  const [showMemberships, setShowMemberships] = useState(false);

  useEffect(() => {
    setPickupDateTime(null);
    setSelectedLegSlots({});
  }, [tripType]);

  const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <button
      className="border rounded-full px-3 py-2 text-white bg-black w-[240px] text-left"
      onClick={onClick}
      ref={ref}
      type="button"
    >
      {value || placeholder}
    </button>
  ));

  // fetch destinations
  useEffect(() => {
    (async () => {
      try {
        setLoadingDest(true);
        setError('');
        setEmpty(false);

        const res = await fetch(routesUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (!Array.isArray(list)) throw new Error('Invalid payload');

        const normalized = list.map((d) => ({ ...d, id: String(d.id) }));
        setDestinations(normalized);

        if (normalized.length === 0) setEmpty(true);
        setSelectedDestinationId('');
      } catch (e) {
        console.error('Failed to load destinations', e);
        setError('Failed to load routes');
        setSelectedDestinationId('');
      } finally {
        setLoadingDest(false);
      }
    })();
  }, [routesUrl]);

  const isOneWay = tripType === 'oneway';

  const selectedDestination = destinations.find(
    (d) => d.id === String(selectedDestinationId)
  );
  const oneWaySupported =
    !selectedDestination ||
    Number(selectedDestination.one_way_price_cents || 0) > 0;

  const datesValid = isOneWay
    ? pickupDateTime instanceof Date
    : Array.isArray(pickupDateTime) &&
      pickupDateTime?.[0] instanceof Date &&
      pickupDateTime?.[1] instanceof Date;

  const canSearch =
    !!selectedDestinationId && !loadingDest && datesValid && passengers >= 1;

  // TIMEZONE-SAFE DATE FORMATTER
  const fmtWords = (s) => {
    if (!s) return '';
    const str = String(s);

    // "YYYY-MM-DD" -> parse as LOCAL date (no UTC shift)
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [_, y, mo, d] = m;
      const dt = new Date(Number(y), Number(mo) - 1, Number(d));
      return dt.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    // fallback
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const fmtMoney = (amount) =>
    amount.toLocaleString(undefined, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    });

  const fetchJson = async (url, { timeoutMs = 10000 } = {}) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(t);
    }
  };

  // availability fetch helper
  const fetchAvailability = async (destination_id, date, leg = 'outbound') => {
    const url = `${API_BASE}/availability?destination_id=${encodeURIComponent(
      destination_id
    )}&date=${encodeURIComponent(date)}&leg=${encodeURIComponent(leg)}`;
    return fetchJson(url, { timeoutMs: 10000 });
  };

  const fetchResults = async (q) => {
    try {
      setResultsLoading(true);
      setResultsError('');
      setDays([]);
      setDestinationMeta(null);
      setSelectedLegSlots({});

      const destId = q.destination_id;
      const isOneWaySearch = q.trip_type === 'oneway';
      const date1 = String(q.start_date).slice(0, 10);
      const date2 =
        q.trip_type !== 'oneway' && q.end_date
          ? String(q.end_date).slice(0, 10)
          : null;

      const [r1Err, a1] = await (async () => {
        try {
          return [null, await fetchAvailability(destId, date1, 'outbound')];
        } catch (e) {
          console.error('availability(outbound) failed', e);
          return [e, null];
        }
      })();

      const [r2Err, a2] = await (async () => {
        if (!date2) return [null, null];
        try {
          return [null, await fetchAvailability(destId, date2, 'return')];
        } catch (e) {
          console.error('availability(return) failed', e);
          return [e, null];
        }
      })();

      if (a1?.destination) setDestinationMeta(a1.destination);

      const dayBlocks = [];
            if (a1?.date) {
        dayBlocks.push({
          date: a1.date,
          leg: 'outbound',
          slots: Array.isArray(a1.slots)
            ? a1.slots.filter((s) => {
                // always require seats
                if ((s?.seats_left ?? 0) <= 0) return false;
                // for ONE-WAY searches, hide 0-cent slots so they look "unavailable"
                if (isOneWaySearch && (s?.price_cents ?? 0) <= 0) return false;
                return true;
              })
            : [],
        });
      }

      if (a2?.date) {
        dayBlocks.push({
          date: a2.date,
          leg: 'return',
          slots: Array.isArray(a2.slots)
            ? a2.slots.filter(
                (s) => (s?.seats_left ?? 0) > 0 // no price filter here
              )
            : [],
        });
      }

      // Ensure outbound appears first if both legs exist
      if (dayBlocks.length === 2 && date2) {
        dayBlocks.sort((x) => (x.leg === 'outbound' ? -1 : 1));
      }

      setDays(dayBlocks);

      if (r1Err && !a1) {
        setResultsError('Could not load outbound trips. Please try again.');
        return;
      }
      if (date2 && r2Err && !a2) {
        setResultsError('Outbound loaded, but return trips failed to load.');
        return;
      }
      if (
        dayBlocks.length === 0 ||
        dayBlocks.every((d) => (d.slots?.length || 0) === 0)
      ) {
        setResultsError('No trips found with seats for those date(s).');
      }
    } catch (e) {
      console.error('Search error:', e);
      setResultsError('Failed to load results. Please try again.');
    } finally {
      setResultsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!canSearch) {
      alert('Please select a route, valid date(s), and passengers.');
      return;
    }

    let obj;
    if (isOneWay) {
      obj = {
        trip_type: 'oneway',
        destination_id: String(selectedDestinationId),
        start_date: pickupDateTime.toISOString(),
        passengers: String(passengers),
      };
    } else {
      const [start, end] = pickupDateTime;
      obj = {
        trip_type: 'return',
        destination_id: String(selectedDestinationId),
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        passengers: String(passengers),
      };
    }

    setQuery(obj);
    setShowResults(true);
    setShowPrivate(false);
    setShowMemberships(false);
    setSelectedLegSlots({});
    onSearch?.(obj);
    await fetchResults(obj);
  };

  // pick a time slot
  const handleSelectSlot = (legKey, dayDate, slot) => {
    if (isOneWay) {
      setConfirmData({ date: dayDate, slot });
      setConfirmOpen(true);
      return;
    }

    // toggle one slot per LEG
    setSelectedLegSlots((prev) => {
      const prevSlot = prev[legKey]?.time_slot;
      if (prevSlot === slot.time_slot) {
        const copy = { ...prev };
        delete copy[legKey];
        return copy;
      }
      return {
        ...prev,
        [legKey]: {
          date: dayDate,
          time_slot: slot.time_slot,
          price_cents: slot.price_cents,
          currency: slot.currency || 'CAD',
        },
      };
    });
  };

  // auto-open round-trip confirm when both legs picked
  useEffect(() => {
    if (!query || isOneWay || !showResults) return;
    const hasBoth =
      !!selectedLegSlots.outbound?.time_slot &&
      !!selectedLegSlots.return?.time_slot;
    if (hasBoth) setConfirmReturnOpen(true);
  }, [query, isOneWay, showResults, selectedLegSlots]);

  // navigate to checkout (oneway)
  const confirmCheckoutOneWay = () => {
    if (!query || !confirmData?.slot?.time_slot) return;
    const carry = new URLSearchParams({
      destination_id: String(query.destination_id),
      service_date: String(confirmData.date),
      time_slot: String(confirmData.slot.time_slot),
      passengers: String(query.passengers),
      trip_type: 'oneway',
    });
    navigate(`/checkout?${carry.toString()}`);
  };

  // navigate to checkout (return)
  const confirmCheckoutReturn = () => {
    if (!query) return;
    const out = selectedLegSlots.outbound;
    const ret = selectedLegSlots.return;
    if (!out?.time_slot || !ret?.time_slot) return;

    const carry = new URLSearchParams({
      destination_id: String(query.destination_id),
      outbound_date: String(out.date),
      outbound_time: out.time_slot,
      return_date: String(ret.date),
      return_time: ret.time_slot,
      passengers: String(query.passengers),
      trip_type: 'return',
    });
    navigate(`/checkout?${carry.toString()}`);
  };

  // styles
  const tripButtonStyle = (type) => {
    const active = tripType === type;
    return `
      relative px-6 py-2 rounded-full font-semibold border transition-all duration-300
      ${active ? 'bg-white text-black border-transparent' : 'bg-white text-black'}
    `;
  };
  const overlayStyle = (type) =>
    tripType === type
      ? ''
      : 'after:absolute after:inset-0 after:bg-gray-800/40 after:rounded-full after:pointer-events-none';

  const togglePrivate = () => {
    setShowPrivate((prev) => {
      const next = !prev;
      if (next) {
        setShowResults(false);
        setShowMemberships(false);
      }
      return next;
    });
  };

  const toggleMemberships = () => {
    setShowMemberships((prev) => {
      const next = !prev;
      if (next) {
        setShowResults(false);
        setShowPrivate(false);
      }
      return next;
    });
  };

  const panelsOpen = showPrivate || showMemberships;

  return (
    <div className="container mx-auto mt-4">
      {/* Trip Type Selector */}
      <div className="flex items-center justify-between px-2 pt-4 mb-4 relative z-20">
        <div className="flex gap-3">
          <button
            className={`${tripButtonStyle('oneway')} ${overlayStyle('oneway')}`}
            onClick={() => setTripType('oneway')}
            type="button"
            disabled={panelsOpen}
          >
            One-way
          </button>
          <button
            className={`${tripButtonStyle('return')} ${overlayStyle('return')}`}
            onClick={() => setTripType('return')}
            type="button"
            disabled={panelsOpen}
          >
            Return
          </button>
        </div>

        {/* Desktop toggles */}
        <div className="hidden md:flex gap-2">
          <button
            onClick={toggleMemberships}
            className="px-6 py-2 rounded-full font-semibold bg-white text-black hover:bg-white/90 transition"
            type="button"
          >
            {showMemberships ? 'Back to shared shuttles' : 'Memberships'}
          </button>
          <button
            onClick={togglePrivate}
            className="px-6 py-2 rounded-full font-semibold bg-white text-black hover:bg-white/90 transition"
            type="button"
          >
            {showPrivate ? 'Back to shared shuttles' : 'Private Bookings'}
          </button>
        </div>
      </div>

      {/* Background image + overlay */}
      <div
        className="mx-2 relative bg-cover bg-center rounded-2xl overflow-hidden md:overflow-visible min-h-[22rem] md:min-h-[26rem] transition-all duration-700 ease-in-out pb-8"
        style={{ backgroundImage: 'url("/bg-mountain.png")' }}
      >
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />

        <div className="relative flex flex-col items-center mt-6 pb-6 h-full z-20">
          {/* Search Controls — HIDDEN while a panel is open */}
          {!panelsOpen && (
            <div className="bg-black/70 rounded-full mt-6 px-4 py-3 max-w-6xl w-[95%] md:w-auto flex flex-col md:flex-row items-center justify-evenly">
              {/* Destinations Dropdown */}
              <div className="flex flex-col items-center md:flex-row md:space-x-4">
                <span className="text-white font-semibold">Routes:</span>
                <div className="relative">
                  <select
                    className="appearance-none border rounded-full px-4 py-2 bg-black text-white w-[260px] pr-10"
                    value={selectedDestinationId}
                    onChange={(e) =>
                      setSelectedDestinationId(String(e.target.value))
                    }
                    disabled={loadingDest}
                  >
                    <option value="" disabled={!empty}>
                      {loadingDest
                        ? 'Loading…'
                        : empty
                        ? 'No routes yet'
                        : 'Select route'}
                    </option>

                    {!loadingDest &&
                      !empty &&
                      destinations.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/70">
                    ▾
                  </span>
                </div>
              </div>

              {/* Date Picker */}
              <div className="flex flex-col items-center md:flex-row md:space-x-2">
                <span className="text-white font-semibold">
                  {tripType === 'oneway' ? 'Date:' : 'Dates:'}
                </span>
                {tripType === 'oneway' ? (
                  <DatePicker
                    selected={pickupDateTime}
                    onChange={(date) => setPickupDateTime(date)}
                    placeholderText="Select trip date"
                    minDate={new Date()}
                    dateFormat="MMMM d, yyyy"
                    customInput={<CustomInput placeholder="Select trip date" />}
                    showPopperArrow={false}
                    withPortal
                    portalId="root-portal"
                    popperClassName="z-[9999]"
                  />
                ) : (
                  <DatePicker
                    selectsRange
                    startDate={pickupDateTime?.[0] || null}
                    endDate={pickupDateTime?.[1] || null}
                    onChange={(update) => setPickupDateTime(update)}
                    placeholderText="Select trip dates"
                    minDate={new Date()}
                    dateFormat="MMMM d, yyyy"
                    customInput={<CustomInput placeholder="Select trip dates" />}
                    showPopperArrow={false}
                    withPortal
                    portalId="root-portal"
                    popperClassName="z-[9999]"
                  />
                )}
              </div>

              {/* Passenger Count */}
              <div className="flex items-center gap-2 bg-black rounded-full px-3 py-2">
                <span className="text-white font-semibold">Passengers:</span>
                <div className="flex items-center">
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-white/10 text-white border border-white/20 hover:bg-white/20 disabled:opacity-40"
                    onClick={() =>
                      setPassengers((p) => Math.max(1, p - 1))
                    }
                    disabled={passengers <= 1}
                  >
                    −
                  </button>
                  <span className="mx-3 text-white min-w-[1.5rem] text-center">
                    {passengers}
                  </span>
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-white/10 text-white border border-white/20 hover:bg-white/20 disabled:opacity-40"
                    onClick={() =>
                      setPassengers((p) => Math.min(8, p + 1))
                    }
                    disabled={passengers >= 8}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Search Button */}
              <button
                className={`p-6 rounded-full lg:p-2 ${
                  canSearch
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-400 text-gray-300 cursor-not-allowed'
                }`}
                onClick={handleSearch}
                disabled={!canSearch}
                aria-label="Search"
                title="Search"
                type="button"
              >
                <MagnifyingGlassIcon className="h-6 w-6" />
              </button>
            </div>
          )}

          {/* Optional error/empty banners */}
          {!loadingDest && error && !empty && !panelsOpen && (
            <div className="mt-3 text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          {!loadingDest && empty && !panelsOpen && (
            <div className="mt-3 text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
              No routes configured yet. Add destinations in the admin.
            </div>
          )}

          {/* Inline Results White Box */}
          {showResults && !panelsOpen && (
            <div className="relative z-10 mt-4 px-4 flex justify-center w-full">
              <div className="w-full max-w-5xl mx-auto rounded-3xl bg-white text-black shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-gray-200 p-6 md:p-10 overflow-y-auto max-h-[70vh]">
                <button
                  onClick={() => setShowResults(false)}
                  className="absolute top-4 right-6 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                  aria-label="Close results"
                >
                  ✕
                </button>

                {/* Query summary */}
                {query && (
                  <div className="text-sm text-black/70 mb-6 flex flex-wrap gap-4">
                    {query.trip_type === 'oneway' ? (
                      <span>
                        <span className="font-semibold">Date:</span>{' '}
                        {fmtWords(query.start_date)}
                      </span>
                    ) : (
                      <>
                        <span>
                          <span className="font-semibold">Outbound:</span>{' '}
                          {fmtWords(query.start_date)}
                        </span>
                        <span>
                          <span className="font-semibold">Return:</span>{' '}
                          {fmtWords(query.end_date)}
                        </span>
                      </>
                    )}
                    <span>
                      <span className="font-semibold">Route:</span>{' '}
                      {destinationMeta?.name ||
                        `Destination #${query.destination_id}`}
                    </span>
                    <span>
                      <span className="font-semibold">Passengers:</span>{' '}
                      {query.passengers}
                    </span>
                  </div>
                )}

                {/* Loading / error / days */}
                {resultsLoading && (
                  <div className="text-center text-lg text-gray-500 py-20">
                    Loading trips…
                  </div>
                )}

                {!resultsLoading && resultsError && (
                  <div className="text-center text-red-600 bg-red-50 border border-red-200 rounded-xl p-6">
                    {resultsError}
                  </div>
                )}

                {!resultsLoading && !resultsError && days.length > 0 && (
                  <div className="space-y-6">
                    {days.map((day) => {
                      const selectedForLeg =
                        selectedLegSlots[day.leg]?.time_slot || null;
                      const legLabel =
                        day.leg === 'outbound' ? 'Outbound' : 'Return';

                      return (
                        <div
                          key={`${day.date}-${day.leg}`}
                          className="rounded-2xl border border-gray-200 bg-white p-4"
                        >
                          <div className="font-semibold mb-1">
                            {legLabel}
                          </div>
                          <div className="text-sm text-gray-600 mb-3">
                            {fmtWords(day.date)}
                          </div>
                          {day.slots.length === 0 ? (
                            <div className="rounded-xl border border-gray-200 bg-white p-4 text-gray-500">
                              No departures
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-3">
                              {day.slots.map((slot) => {
                                const pillSelected =
                                  !isOneWay &&
                                  selectedForLeg === slot.time_slot;
                                return (
                                  <button
                                    key={`${day.date}-${day.leg}-${slot.time_slot}`}
                                    className={`inline-flex items-center gap-3 rounded-full px-4 py-2 transition
                                      ${
                                        isOneWay
                                          ? 'bg-black text-white hover:bg-black/90'
                                          : pillSelected
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : 'bg-black text-white hover:bg-black/90'
                                      }`}
                                    onClick={() =>
                                      handleSelectSlot(
                                        day.leg,
                                        day.date,
                                        slot
                                      )
                                    }
                                  >
                                    <span className="font-semibold">
                                      {slot.time_slot}
                                    </span>
                                    <span className="text-white/70">•</span>
                                    <span className="text-white/80">
                                      {slot.seats_left} left
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Private Bookings White Box */}
          {showPrivate && (
            <div className="relative z-10 mt-4 px-4 flex justify-center w-full">
              <div className="w-full max-w-5xl mx-auto rounded-3xl bg-white text-black shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-gray-200 p-6 md:p-10 overflow-y-auto max-h-[70vh]">
                <button
                  onClick={() => setShowPrivate(false)}
                  className="absolute top-4 right-6 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                  aria-label="Close"
                >
                  ✕
                </button>

                <h2 className="text-xl md:text-2xl font-semibold">
                  Private Booking
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Flat-rate private shuttles. Choose one-way or round trip.
                  Last-minute (within 24h) adds {HOURLY.lastMinuteSurchargePct}
                  %.
                </p>

                {/* Resort fixed pricing */}
                <div className="mt-5 grid gap-4 md:grid-cols-3">
                  {PRIVATE_RATES.map((r) => (
                    <div
                      key={r.name}
                      className="rounded-2xl border border-gray-200 p-4"
                    >
                      <div className="font-semibold">{r.name}</div>
                      <div className="mt-2 text-sm">
                        <div>
                          One way:{' '}
                          <span className="font-semibold">
                            {fmtMoney(r.oneway)}
                          </span>
                        </div>
                        <div>
                          Round trip:{' '}
                          <span className="font-semibold">
                            {fmtMoney(r.round)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Hourly section */}
                <div className="mt-6 rounded-2xl border border-gray-200 p-4">
                  <div className="font-semibold mb-2">
                    Private Shuttles (hourly)
                  </div>
                  <ul className="text-sm list-disc pl-5 space-y-1">
                    <li>Minimum {HOURLY.minHours} hours</li>
                    <li>
                      {fmtMoney(HOURLY.standardPerHour)} / hour (before 10pm)
                    </li>
                    <li>
                      After 10pm: {fmtMoney(HOURLY.after10pmPerHour)} / hour
                    </li>
                    <li>8-hour booking: {fmtMoney(HOURLY.day8Hours)}</li>
                    <li className="text-gray-600">
                      Last-minute (within 24h): +
                      {HOURLY.lastMinuteSurchargePct}%
                    </li>
                  </ul>
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    href={PRIVATE_FORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  >
                    Request a private booking
                  </a>
                  <button
                    onClick={() => setShowPrivate(false)}
                    className="rounded-full px-4 py-2 border border-gray-300 hover:bg-gray-100"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Memberships White Box */}
          {showMemberships && (
            <div className="relative z-10 mt-4 px-4 flex justify-center w-full">
              <div className="w-full max-w-5xl mx-auto rounded-3xl bg-white text-black shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-gray-200 p-6 md:p-10 overflow-y-auto max-h-[70vh]">
                <button
                  onClick={() => setShowMemberships(false)}
                  className="absolute top-4 right-6 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                  aria-label="Close"
                >
                  ✕
                </button>

                <h2 className="text-xl md:text-2xl font-semibold">
                  Memberships
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Student membership pricing.
                </p>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 p-4">
                    <div className="text-lg font-semibold">
                      Student Membership
                    </div>
                    <ul className="mt-3 text-sm text-gray-700 space-y-2">
                      <li>
                        <span className="font-semibold">$699</span> paid
                        upfront (4 months)
                      </li>
                      <li>
                        <span className="font-semibold">$199</span> per month
                      </li>
                    </ul>

                    <a
                      href={MEMBERSHIP_FORM_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-block rounded-full px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    >
                      Join now
                    </a>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => setShowMemberships(false)}
                    className="rounded-full px-4 py-2 border border-gray-300 hover:bg-gray-100"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* One-way confirmation modal */}
          {confirmOpen && confirmData && (
            <div className="fixed inset-0 z-50 grid place-items-center">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setConfirmOpen(false)}
              />
              <div className="relative w-full max-w-md mx-auto rounded-2xl bg-white text-black shadow-2xl border border-gray-200 p-6">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="absolute top-3 right-3 rounded-full border border-gray-300 px-2 py-0.5 text-sm hover:bg-gray-100"
                  aria-label="Close"
                >
                  ✕
                </button>

                <div className="text-lg font-semibold mb-2">
                  Confirm your shuttle
                </div>

                <div className="space-y-2 text-sm text-gray-700">
                  <div>
                    <span className="font-semibold">Route:</span>{' '}
                    {destinationMeta?.name ||
                      `Destination #${query?.destination_id}`}
                  </div>
                  <div>
                    <span className="font-semibold">Date:</span>{' '}
                    {fmtWords(confirmData.date)}
                  </div>
                  <div>
                    <span className="font-semibold">Time:</span>{' '}
                    {confirmData.slot.time_slot}
                  </div>
                  <div>
                    <span className="font-semibold">Passengers:</span>{' '}
                    {query?.passengers}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="rounded-full px-4 py-2 border border-gray-300 hover:bg-gray-100"
                    onClick={() => setConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="rounded-full px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    onClick={confirmCheckoutOneWay}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Return confirmation modal */}
          {confirmReturnOpen && query && (
            <div className="fixed inset-0 z-50 grid place-items-center">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setConfirmReturnOpen(false)}
              />
              <div className="relative w-full max-w-md mx-auto rounded-2xl bg-white text-black shadow-2xl border border-gray-200 p-6">
                <button
                  onClick={() => setConfirmReturnOpen(false)}
                  className="absolute top-3 right-3 rounded-full border border-gray-300 px-2 py-0.5 text-sm hover:bg-gray-100"
                  aria-label="Close"
                >
                  ✕
                </button>

                {(() => {
                  const out = selectedLegSlots.outbound;
                  const ret = selectedLegSlots.return;
                  const startKey = out?.date || String(query.start_date).slice(0, 10);
                  const endKey = ret?.date || String(query.end_date || query.start_date).slice(0, 10);

                  return (
                    <>
                      <div className="text-lg font-semibold mb-2">
                        Confirm your round trip
                      </div>
                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <span className="font-semibold">Route:</span>{' '}
                          {destinationMeta?.name ||
                            `Destination #${query?.destination_id}`}
                        </div>
                        <div className="rounded-xl border border-gray-200 p-3">
                          <div className="font-semibold mb-1">Outbound</div>
                          <div>
                            <span className="font-semibold">Date:</span>{' '}
                            {fmtWords(startKey)}
                          </div>
                          <div>
                            <span className="font-semibold">Time:</span>{' '}
                            {out?.time_slot || '—'}
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200 p-3">
                          <div className="font-semibold mb-1">Return</div>
                          <div>
                            <span className="font-semibold">Date:</span>{' '}
                            {fmtWords(endKey)}
                          </div>
                          <div>
                            <span className="font-semibold">Time:</span>{' '}
                            {ret?.time_slot || '—'}
                          </div>
                        </div>
                        <div>
                          <span className="font-semibold">Passengers:</span>{' '}
                          {query?.passengers}
                        </div>
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                          <div className="font-semibold">
                            Round-trip price per seat:
                          </div>
                          <div className="text-blue-900">
                            {Intl.NumberFormat(undefined, {
                              style: 'currency',
                              currency: 'CAD',
                              minimumFractionDigits: 0,
                            }).format(
                              Number(
                                destinationMeta?.return_price_cents || 0
                              ) / 100
                            )}
                          </div>
                          <div className="text-xs text-blue-900/70 mt-1">
                            Covers both outbound and return legs.
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          className="rounded-full px-4 py-2 border border-gray-300 hover:bg-gray-100"
                          onClick={() => setConfirmReturnOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="rounded-full px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                          onClick={confirmCheckoutReturn}
                        >
                          Checkout
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
